import { useState } from 'react';
import { useRoute, Link } from 'wouter';
import { trpc } from '../../lib/trpc';
import { useAsync } from '../../lib/useAsync';
import { Loading, ErrorBox, StatusPill, PageTitle } from '../../components/admin/ui';
import {
  TICKET_STATUS_LABEL,
  TICKET_STATUS_COLOR,
  PRIORIDADE_LABEL,
  PRIORIDADE_COLOR,
  AUTOR_LABEL,
} from '../../lib/format';

export function AdminTicketDetalhe() {
  const [, params] = useRoute('/admin/suporte/:id');
  const id = params?.id ?? '';
  const { data, loading, error, reload } = useAsync(() => trpc.admin.ticket.query({ id }), [id]);
  const [aviso, setAviso] = useState<string | null>(null);
  const placeholder = (a: string) =>
    setAviso(`"${a}": ação real vem na próxima fase (esta etapa é só estrutura + dados).`);

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-2">
        <Link href="/admin/suporte" className="text-sm text-ink-3 hover:text-ink">
          ← Suporte com IA
        </Link>
      </div>

      {loading && <Loading />}
      {error && <ErrorBox msg={error} onRetry={reload} />}
      {data === null && !loading && <ErrorBox msg="Ticket não encontrado." />}

      {data && (
        <>
          <PageTitle titulo={data.ticket.assunto} />

          <div className="mb-4 flex flex-wrap items-center gap-4">
            <StatusPill label={TICKET_STATUS_LABEL[data.ticket.status]!} color={TICKET_STATUS_COLOR[data.ticket.status]!} />
            <span className="text-sm font-medium" style={{ color: PRIORIDADE_COLOR[data.ticket.prioridade] }}>
              Prioridade {PRIORIDADE_LABEL[data.ticket.prioridade]}
            </span>
          </div>

          <div className="grid gap-5 lg:grid-cols-[1.4fr_1fr]">
            {/* Conversa */}
            <div className="card p-5">
              <div className="text-[11px] uppercase tracking-wide text-ink-4">Conversa</div>
              <ul className="mt-4 space-y-4">
                {data.mensagens.map((m, i) => {
                  const daIA = m.autor === 'ia';
                  const doCliente = m.autor === 'cliente';
                  return (
                    <li key={i} className={`flex ${doCliente ? 'justify-start' : 'justify-end'}`}>
                      <div
                        className="max-w-[85%] rounded-lg px-4 py-2.5 text-sm"
                        style={{
                          background: doCliente ? 'var(--bg-warm)' : daIA ? 'var(--accent-soft)' : 'var(--surface)',
                          border: '1px solid var(--line)',
                          color: 'var(--ink)',
                        }}
                      >
                        <div className="mb-1 text-[11px] font-medium text-ink-3">
                          {AUTOR_LABEL[m.autor] ?? m.autor} ·{' '}
                          {new Date(m.data).toLocaleDateString('pt-BR')}
                        </div>
                        {m.conteudo}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Painel do agente de IA + ações */}
            <div className="space-y-5">
              <div className="card p-5">
                <div className="flex items-center gap-2">
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded text-[10px] font-bold text-white"
                    style={{ background: 'var(--accent)' }}
                  >
                    IA
                  </span>
                  <span className="text-[11px] uppercase tracking-wide text-ink-4">Sugestão de resposta</span>
                </div>
                <p className="mt-3 rounded-sm border border-line px-4 py-3 text-sm leading-relaxed text-ink-2" style={{ background: 'var(--accent-soft)' }}>
                  {data.ticket.sugestaoIa ?? 'Sem sugestão para este ticket.'}
                </p>
                <div className="mt-2 text-[11px] text-ink-4">
                  Texto de exemplo. A geração real com LLM entra na próxima fase.
                </div>
                <button
                  onClick={() => placeholder('Usar sugestão e enviar resposta')}
                  className="btn btn-primary mt-4 w-full"
                >
                  Usar sugestão e responder
                </button>
              </div>

              <div className="card p-5">
                <div className="text-[11px] uppercase tracking-wide text-ink-4">Ações</div>
                <div className="mt-3 space-y-2">
                  <BotaoAcao label="Enviar resposta" onClick={() => placeholder('Enviar resposta')} />
                  <BotaoAcao label="Marcar como resolvido" onClick={() => placeholder('Marcar como resolvido')} />
                  <BotaoAcao label="Escalar para humano" onClick={() => placeholder('Escalar para humano')} />
                </div>
                {aviso && <div className="mt-3 rounded-sm bg-bg-warm px-3 py-2 text-xs text-ink-3">{aviso}</div>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function BotaoAcao({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-sm border border-line-2 px-4 py-2.5 text-left text-sm text-ink transition-colors hover:bg-bg-warm"
    >
      {label} <span className="text-ink-3">→</span>
    </button>
  );
}
